/* eslint-disable no-cond-assign */
/* eslint-disable import/no-extraneous-dependencies */
import Icon, { CloseCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Button, message, Drawer } from "antd";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import EngagedAI from "../../../../static/EngagedAi_trans_bg.svg";
import { GET_AI_RESPONSE, GET_USE_CASES, GET_LEAD_AI_OTTO_OVERVIEW } from "../../../graphql/query";
import useLang from "../../../hooks/useLang";
import { updateOtto360OverviewRedux } from "../../../redux/slices/leadSlice";

// Helper function to convert text to Title Case
const toTitleCase = str => {
  return str.replace(/\w\S*/g, txt => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

// Common function to format text with proper line breaks, bold topics, and indentation leadSummaryText
const formatTextContent = text => {
  // Validate input: return empty string if text is null, undefined, or not a string
  if (!text || typeof text !== "string") {
    return "";
  }

  // Replace 'leadSummaryText' with 'Lead Conversation Summary' and add newline
  const formattedText = text.replace(/leadSummaryText/gi, "Lead Conversation Summary\n");

  return formattedText
    .replace(
      /### (.+?)(?=\\n|\\r|\n|\r|$)/g,
      (match, title) => `<strong>${toTitleCase(title.trim())}</strong>`
    ) // Handle ### headings BEFORE line break conversion
    .replace(/\\n\\n/g, "<br><br>")
    .replace(/\\n/g, "<br>")
    .replace(/\n\n/g, "<br><br>")
    .replace(/\n/g, "<br>")
    .replace(/- \*\*(.*?)\*\*/g, "- <strong>$1</strong>") // Bold topics without indent
    .replace(/- (?!<strong>)/g, "&nbsp;&nbsp;&nbsp;&nbsp;- "); // Indent non-bold bullets
};

// Function to truncate text to show only first few lines
const getTruncatedText = (text, maxLength = 140) => {
  if (!text) return "";
  const plainText = text.replace(/<[^>]*>?/gm, "").replace(/\\n/g, " ");
  if (plainText.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

function LeadSummaryOpenAi({ selectedLead, user, text, labelText }) {
  // const { user } = useContext(UserContext);
  const dispatch = useDispatch();
  const leads = useSelector(state => state.lead.leads);
  const { data: useCases } = useQuery(GET_USE_CASES);
  const [lang] = useLang();
  const [misc, setmisc] = useState({
    loading: false,
    isShowLeadSummarySection: false,
    leadSummaryText: "",
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [generatedSummeryLead, setGeneratedSummeryLead] = useState(null);
  const [getAIResponse, { refetch: refetchAIResponse, loading: aiResponseLoading }] = useLazyQuery(
    GET_AI_RESPONSE,
    {
      fetchPolicy: "network-only",
      variables: {
        companyId: +user?.company?.id,
        applicationUseCaseId: +useCases?.getApplicationUseCases?.filter(
          useCase => useCase.code === "ENGAGEMENT_CUSTOMER_SUMMARY"
        )[0]?.id,
        leadId: selectedLead?.id,
      },
      onCompleted: res => {
        if (res?.getAiResponse?.statusCode !== 200) {
          message.error(lang.ottoAiSummaryFailedText);
        }
      },
    }
  );

  const [
    getLeadAIOttoOverview,
    { refetch: refetchLeadAIOttoOverview, loading: leadAIOttoOverviewLoading },
  ] = useLazyQuery(GET_LEAD_AI_OTTO_OVERVIEW, {
    fetchPolicy: "network-only",
    variables: {
      companyId: +user?.company?.id,
      locationId: +selectedLead?.location?.id,
      leadId: selectedLead?.id,
      applicationUseCaseId: +useCases?.getApplicationUseCases?.filter(
        useCase => useCase.code === "ENGAGEMENT_CUSTOMER_SUMMARY"
      )[0]?.id,
    },
    onCompleted: res => {
      const content = res?.getAiOttoOverview?.responseText || "";
      if (content) {
        // Save to Redux immediately
        const overviewData = {
          content,
          timestamp: new Date().toISOString(),
          typeObj: false,
        };

        dispatch(
          updateOtto360OverviewRedux({
            leadId: selectedLead?.id,
            otto360Overview: overviewData,
          })
        );

        setmisc({
          loading: false,
          isShowLeadSummarySection: true,
          leadSummaryText: content,
          typeObj: false,
        });
      } else {
        setmisc({
          loading: false,
          isShowLeadSummarySection: false,
          leadSummaryText: "",
          typeObj: false,
        });
      }
    },
  });

  useEffect(() => {
    if (!selectedLead?.id || !user?.company?.id) return;

    // Check Redux first
    const leadFromStore = leads.find(lead => +lead.id === +selectedLead.id);
    const otto360Overview = leadFromStore?.otto360Overview;

    // If not in Redux, fetch from DB
    if (!otto360Overview) {
      getLeadAIOttoOverview();
    }
  }, [selectedLead?.id, user?.company?.id]);

  // Load saved overview from leads array by matching selectedLead.id
  useEffect(() => {
    if (!selectedLead?.id) return;

    // Find the lead in the leads array with type-safe comparison
    const leadFromStore = leads.find(lead => +lead.id === +selectedLead.id);
    const otto360Overview = leadFromStore?.otto360Overview;

    if (otto360Overview) {
      if (otto360Overview.typeObj && otto360Overview.parsedObj) {
        setmisc({
          loading: false,
          isShowLeadSummarySection: true,
          parsedObj: otto360Overview.parsedObj,
          typeObj: true,
          leadSummaryText: "",
        });
      } else if (otto360Overview.content) {
        setmisc({
          loading: false,
          isShowLeadSummarySection: true,
          leadSummaryText: otto360Overview.content,
          typeObj: false,
        });
      }
    } else {
      // Reset state when no saved overview for this specific lead
      setmisc({
        loading: false,
        isShowLeadSummarySection: false,
        leadSummaryText: "",
        parsedObj: undefined,
        typeObj: false,
      });
    }
  }, [selectedLead?.id, leads]);
  const createLeadSummary = async () => {
    setGeneratedSummeryLead(selectedLead);
    try {
      setmisc({ ...misc, loading: true, isShowLeadSummarySection: false });
      const parseResponse = async conversation => {
        const strippedConversation = conversation.replace(/<[^>]+>/g, ""); // removes all HTML tags

        const headingRegex = /<(.*?)-start>(.*?)<(.*?)-end>/g; // regular expression to match heading tags and their content

        const headings = {};
        let match;
        while ((match = headingRegex.exec(conversation)) !== null) {
          const [, heading, content] = match;
          headings[heading] = content.trim();
        }
        return headings;
      };

      const response = await getAIResponse();
      const headingRegex = /<(.*?)-start>(.*?)<(.*?)-end>/g; // regular expression to match heading tags and their content
      const content = response?.data?.getAiResponse?.content;
      const overviewData = {
        content,
        timestamp: new Date().toISOString(),
      };

      if ((headingRegex.exec(content) || [])?.length > 0) {
        const parsedObj = await parseResponse(content);
        overviewData.parsedObj = parsedObj;
        overviewData.typeObj = true;
        setmisc({
          ...misc,
          isShowLeadSummarySection: true,
          parsedObj,
          typeObj: true,
        });
      } else {
        overviewData.parsedObj = {
          leadSummaryText: content,
        };
        overviewData.typeObj = !!content;
        setmisc({
          ...misc,
          isShowLeadSummarySection: true,
          leadSummaryText: content,
          typeObj: false,
        });
      }

      // Save to Redux
      dispatch(
        updateOtto360OverviewRedux({
          leadId: selectedLead?.id,
          otto360Overview: overviewData,
        })
      );
    } catch (err) {
      console.log(err.message);
      setmisc({
        ...misc,
        isShowLeadSummarySection: true,
        leadSummaryText:
          "Unable to show the summary at this time. This is an experimental feature, so we appreciate your patience while we're refining things.",
      });
    }
  };
  return (
    <div className="w-full">
      <div className="flex flex-row items-center justify-between w-full">
        <span>{labelText}</span>
        <div className="flex-row-centered gap-1">
          <Button
            loading={aiResponseLoading}
            onClick={() => {
              createLeadSummary();
            }}
            className="flex-row-centered items-center"
          >
            {text || "Otto 360°"} <Icon component={EngagedAI} />
          </Button>
          {/* {misc.isShowLeadSummarySection && (
            <CloseCircleOutlined
              className="cursor-pointer"
              onClick={() => {
                setmisc({ ...misc, isShowLeadSummarySection: false });
                // Clear from Redux store
                dispatch(
                  updateOtto360OverviewRedux({
                    leadId: selectedLead?.id,
                    otto360Overview: null,
                  })
                );
              }}
            />
          )} */}
        </div>
      </div>
      {misc.isShowLeadSummarySection && (
        <div className="w-full text-start">
          <TruncatedContent
            content={
              misc.typeObj
                ? Object.entries(misc.parsedObj)
                    .map(([heading, content]) => `- **${heading}**\n${content}`)
                    .join("\n\n")
                : misc.leadSummaryText
            }
            onReadMore={() => setDrawerVisible(true)}
          />
        </div>
      )}

      {/* Side Drawer for Full Content */}
      <Drawer
        title="Otto 360 Overview"
        placement="right"
        size="large"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setDrawerVisible(false);
              createLeadSummary();
            }}
            loading={aiResponseLoading}
          >
            Regenerate Overview
          </Button>
        }
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setDrawerVisible(false)} type="primary">
              Close
            </Button>
          </div>
        }
      >
        <div className="w-full text-start px-4">
          {misc.typeObj ? (
            <Conversation headings={misc.parsedObj} />
          ) : (
            <div
              className="w-full text-start"
              dangerouslySetInnerHTML={{
                __html: formatTextContent(misc.leadSummaryText),
              }}
            />
          )}
        </div>
      </Drawer>
    </div>
  );
}

export default LeadSummaryOpenAi;

// Component to show truncated plain text content with Read More link
const TruncatedContent = ({ content, onReadMore }) => {
  const truncatedText = getTruncatedText(content, 140);
  const shouldShowReadMore = content && content.length > 140;

  return (
    <div className="w-full text-start">
      <div
        dangerouslySetInnerHTML={{
          __html: formatTextContent(truncatedText),
        }}
      />
      {shouldShowReadMore && (
        <button
          type="button"
          onClick={onReadMore}
          className="text-blue-500 hover:text-blue-700 underline mt-2 cursor-pointer bg-transparent border-none p-0"
        >
          Read More
        </button>
      )}
    </div>
  );
};

const Conversation = ({ headings }) => {
  return (
    <div>
      {Object.entries(headings).map(([heading, content]) => (
        <div key={heading}>
          <h4>{heading}</h4>
          <div
            dangerouslySetInnerHTML={{
              __html: formatTextContent(content),
            }}
          />
        </div>
      ))}
    </div>
  );
};
